import React, { useCallback, useMemo } from 'react';
import { useRef } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { ReportsContext } from 'apps/sigma2-ui/src/reports/hooks/ReportsContext';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { GenerateReportControllerResult } from 'libs/end-to-end/src/generate-report';
import { QueryReportControllerResult } from 'libs/end-to-end/src/generate-report';
import { GenerateReportRequest } from 'libs/end-to-end/src/generate-report';
import { useSessionStorage } from 'apps/sigma2-ui/src/lib/hooks/useSessionStorage';
import { z } from 'zod';
import { PendingReportInfo } from 'apps/sigma2-ui/src/reports/hooks/types';
import { waitForMs } from 'libs/end-to-end/src/generic';
import { usePolling } from 'apps/sigma2-ui/src/lib/hooks/usePolling';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';

interface ReportsFinishedCallback {
  reportId: string;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  fn: (
    reportId: string,
    status: 'success' | 'failure',
    presignedUrl: string | null,
  ) => void;
}
export interface ReportsFinishedCallbacks {
  callbacks: ReportsFinishedCallback[];
}

export const ReportsRenderer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { userAuthenticatedApiCall, isAuthenticated } = useUser();

  const finishedCallbacks = useRef<ReportsFinishedCallbacks>({
    callbacks: [],
  });

  const [pendingReports, setPendingReports] = useSessionStorage({
    key: 'PendingReportsList',
    schema: z.object({ list: z.array(PendingReportInfo) }),
    defaultValue: { list: [] },
  });

  const exportReport = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      params: {
        request: GenerateReportRequest;
        organizationId: string;
        databaseId: string;
        databaseRevisionId: string;
        onStarted?: (reportId: string) => void;
        onFinished?: (
          reportId: string,
          status: 'success' | 'failure',
          presignedUrl: string | null,
        ) => void;
      },
    ) => {
      if (
        !isAlive() ||
        !isAuthenticated ||
        !params.organizationId ||
        !params.databaseId ||
        !params.databaseRevisionId ||
        params.request.charts.length === 0
      ) {
        return;
      }

      const result = await userAuthenticatedApiCall({
        responseSchema: GenerateReportControllerResult,
        url: `${getApiBaseUrl()}/organizations/${params.organizationId}/databases/${params.databaseId}/revisions/${params.databaseRevisionId}/reports`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(params.request),
        },
      });

      if (!isAlive() || !result || result.resultType === 'err') {
        return;
      }

      const reportId = result.value.reportId;

      if (params.onStarted) {
        params.onStarted(reportId);
      }

      // Add the report to the pending reports list.
      setPendingReports((prev) => {
        return {
          list: [
            ...prev.list,
            {
              reportId,
              organizationId: params.organizationId,
              databaseId: params.databaseId,
              databaseRevisionId: params.databaseRevisionId,
              status: 'pending',
              presignedUrl: null,
            },
          ],
        };
      });

      if (params.onFinished) {
        finishedCallbacks.current.callbacks.push({
          reportId,
          fn: params.onFinished,
          organizationId: params.organizationId,
          databaseId: params.databaseId,
          databaseRevisionId: params.databaseRevisionId,
        });
      }
    },
    [isAuthenticated, setPendingReports, userAuthenticatedApiCall],
  );

  const removePendingReport = useCallback(
    (reportId: string) => {
      setPendingReports((prev) => ({
        list: prev.list.filter((report) => report.reportId !== reportId),
      }));
    },
    [setPendingReports],
  );

  const isAlive = useAlive();

  const updatePendingReportsFn = useMemo(() => {
    return async () => {
      if (!isAlive()) {
        return;
      }

      if (pendingReports.list.length === 0) {
        return;
      }

      const newReportsStatus: {
        list: PendingReportInfo[];
      } = { list: [...pendingReports.list] };

      // Loop through the reports and fetch status
      for (const pendingReportListItem of pendingReports.list) {
        const pollResult = await userAuthenticatedApiCall({
          responseSchema: QueryReportControllerResult,
          url: `${getApiBaseUrl()}/organizations/${pendingReportListItem.organizationId}/databases/${pendingReportListItem.databaseId}/revisions/${pendingReportListItem.databaseRevisionId}/reports/${pendingReportListItem.reportId}`,
          requestOptions: {
            method: 'GET',
          },
        });

        if (!isAlive() || !pollResult || pollResult.resultType === 'err') {
          return;
        }

        // Prevent too many requests
        await waitForMs(50);

        if (!isAlive()) {
          return;
        }

        const newStatus = pollResult.value.reportStatus;
        const newPresignedUrl = pollResult.value.presignedUrl;

        if (newStatus === 'success' || newStatus === 'failure') {
          // Call the finished callback if it has been registered (this is for the synchronous case)
          const callbackIndex = finishedCallbacks.current.callbacks.findIndex(
            (callback) => callback.reportId === pendingReportListItem.reportId,
          );

          if (callbackIndex !== -1) {
            const callback = finishedCallbacks.current.callbacks[callbackIndex];
            callback.fn(
              pendingReportListItem.reportId,
              newStatus,
              newPresignedUrl,
            );
            finishedCallbacks.current.callbacks.splice(callbackIndex, 1);
          }
        }

        const reportIndex = newReportsStatus.list.findIndex(
          (report) => report.reportId === pendingReportListItem.reportId,
        );

        if (reportIndex !== -1) {
          newReportsStatus.list[reportIndex] = {
            ...newReportsStatus.list[reportIndex],
            status: newStatus,
            presignedUrl: newPresignedUrl,
          };
        }
      }

      setPendingReports(() => newReportsStatus);
    };
  }, [
    isAlive,
    pendingReports.list,
    setPendingReports,
    userAuthenticatedApiCall,
  ]);

  usePolling({
    action: updatePendingReportsFn,
    delay: 5000,
    // We do our state setting in the action itself.
    onReceivedResult: () => {},
  });

  return (
    <ReportsContext.Provider
      value={{
        exportReport,
        pendingReports: pendingReports.list,
        removePendingReport,
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};
